<template>
  <div id="dictionary-app" 
    :class="[translationToolActive && translationToolLayout === 'side-by-side' ? 'container-fluid translation-tool-active' : 'container']"
  >
    <ImageBanner
            :numberOfResults="this.results != null ? this.results.length : 0"
            :updateSearchType="updateSearchType"
            :searchHistory="this.searchHistory"
            :getSearchTypeText="this.getSearchTypeText"
            :doShowHistoryItem="doShowHistoryItem"
            :limit="searchLimit"
            :setLimit="setLimit"
            :limits="[50,100,200,500,1000,2000]"
            disableAuth
    ></ImageBanner>

    <!-- TRANSLATION TOOL SECTION -->
    <div class="">
      <div
          v-if="this.searchType==='translation-tool' && results && results.length > 0"
          :class="translationToolAreaClasses"
          id="translation-tool-section"
      >
        <div class="translation-tool-controls">
          <div class="btn-group" role="group">
            <button
              class="btn btn-sm"
              :class="translationToolEditMode ? 'btn-outline-secondary' : 'btn-secondary'"
              @click="translationToolEditMode = false"
              v-tooltip="'View mode with clickable terms'"
            >
              <font-awesome-icon icon="search" /> View
            </button>
            <button
              class="btn btn-sm"
              :class="translationToolEditMode ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="handleWillEditTranslationToolText"
              v-tooltip="'Edit mode to modify text'"
            >
              <font-awesome-icon icon="pencil-alt" /> Edit
            </button>
          </div>

          <div class="btn-group" role="group" v-if="!isMobileDevice">
            <button
              class="btn btn-sm"
              :class="translationToolLayout === 'stacked' ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="setTranslationToolLayout('stacked')"
              v-tooltip="'Stacked layout (text on top)'"
            >
              <font-awesome-icon icon="arrow-left" style="transform: rotate(90deg);" />
            </button>
            <button
              class="btn btn-sm"
              :class="translationToolLayout === 'side-by-side' ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="setTranslationToolLayout('side-by-side')"
              v-tooltip="'Side-by-side layout'"
            >
              <font-awesome-icon icon="arrow-left" /> <font-awesome-icon icon="arrow-left" style="transform: scaleX(-1);" />
            </button>
          </div>

          <div class="btn-group" role="group">
            <button
              class="btn btn-sm btn-outline-primary"
              @click="handleTextareaFontSizeDecrease"
              v-tooltip="'Decrease font size'"
            >
              <font-awesome-icon icon="search" style="font-size: 0.8em;" />
            </button>
            <button
              class="btn btn-sm btn-outline-primary"
              @click="handleTextareaFontSizeIncrease"
              v-tooltip="'Increase font size'"
            >
              <font-awesome-icon icon="search" style="font-size: 1.2em;" />
            </button>
          </div>

          <button
            v-if="translationToolEditMode || isEditedTranslationToolText"
            class="btn btn-sm btn-primary"
            @click="handleTextareaSubmit"
            v-tooltip="'Submit edited text'"
          >
            <font-awesome-icon icon="arrow-left" /> Submit
          </button>
        </div>

        <div class="translation-tool-content">
          <textarea
              v-if="translationToolEditMode"
              ref="translationToolTextarea"
              v-model="searchText"
              @change="handleTextAreaChange"
              @input="handleTextAreaInput"
              class="translation-tool-textarea"
              style="field-sizing: content;"
              :style="textAreaStyles"
          ></textarea>
          <TranslationToolTextDisplay
              v-else-if="translationToolText && translationToolText != ''"
              :text="translationToolText"
              :results="results"
              :style="textAreaStyles"
              @term-clicked="scrollToResult"
          />
        </div>
      </div>

      <!-- MAIN CONTENT SECTION -->
      <div
        id="main-content-wrapper"
        :class="mainSearchAndResultsClasses">

        <!-- TITLE ROW -->
        <div class="title-row">
          <h1>Tibetan Classical and Colloquial Translation Dictionary</h1>
          <h5 v-if="preferencesReset" class="preferences-reset-notification">Preferences Reset</h5>
        </div>

        <!-- DEVELOPER MESSAGE ROW -->
        <DeveloperMessage />
        
        <!-- HELP ROW -->
        <div class="justify-content-center">
          <a target="_blank" href="https://docs.google.com/document/d/1x6ezlwocP_edZJwsPykJHqBh4UtTL1A9Lm4zee2DyJM/edit?usp=sharing">Help & Instructions</a>
          <span style="margin: 0 10px;">|</span>
          <router-link to="/updates">Updates</router-link>
        </div>

        <!-- SEARCH CONTROLS ROW -->
        <div class="">
          <SearchInputBox
            :handleSubmit="handleSubmit"
            :clearResults="clearResults"
            :handleClearSearch="clearSearch"
            :getSearchTypeText="getSearchTypeText"
            ref="searchInputBox"
            :results="results"
            :resultsTime="resultsTime"
            :containsOrBeginsWith="containsOrBeginsWith"
          />
        </div>
        <!-- END SEARCH CONTROLS -->

        <!-- FIRST TIME USER TUTORIAL -->
        <FirstTimeUserTutorial
          :dropdownElement="dropdownElement"
          @open-dropdown="handleOpenDropdown"
          @close-dropdown="handleCloseDropdown"
          @tutorial-dismissed="handleTutorialDismissed"
        />


        <!-- ALERTS -->
        <div id="alerts" class="row">
          <div v-if="errorMessage != '' && errorMessage != null && errorMessage != false" class="alert alert-danger" role="alert">
            Ooops, there was an error: {{errorMessage}}
          </div>
        </div>


        <div id="loading" v-if="loading">
          <div class="spinner-border results-loading-spinner" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
        
        <!-- RESULTS -->
        <div v-if="searchType === 'definiendumGetRegexMatches'">
          <div  class="results-container" v-for="result in results" :key="result.dictionary+result.id">
            <Match :result="result" :searchText="searchText" :searchType="searchType"/>
          </div>
        </div>
        <div v-else-if="searchType.includes('monlam')">
          <div  class="results-container" v-for="result in results" :key="result.id">
            <MonlamDefinition :result="result" :searchText="searchText" :searchType="searchType"/>
          </div>
        </div>
        <div v-else id="results-section">
          <!-- Gemini Draft Translation -->
          <GeminiDraftTranslation
            v-if="searchType === 'translation-tool'"
            ref="geminiTranslation"
            :tibetan-text="translationToolText"
            :results="results"
            :enabled="$store.state.app.enableAiTranslation"
          />

          <div v-if="results && results.length > 0">
            <div class="compact-checkbox-container">
              <label class="compact-checkbox-label">
                <input
                  type="checkbox"
                  v-model="compactResultsView"
                  @change="toggleCompactResultsView"
                  class="compact-checkbox"
                >
                <span class="compact-checkbox-text">Toggle compact results view</span>
              </label>
            </div>
            <div v-if="compactResultsView">(click on the Tibetan term to expand/collapse individual rows)</div>
          </div>

          <Definition
              class="results-container"
              :class="{ subsearch: result.subsearchLevel, 'alternative-match': result._isAlternative }"
              :style="{ marginLeft: Number.isInteger(result.subsearchLevel) ? 10*result.subsearchLevel + '%' : 0}"
              v-for="(result, index) in results"
              :key="(result.id || 'nf') +'_'+searchType+'_'+index"
              :result="result"
              :index="index"
              :translateTool="loadTranslateToolSubsearch"
              :small="false"
              @term-hidden="handleTermHidden"
              :compactOneLine="compactResultsView"
          />
          <div v-if="results && results.length > 0 && !noMoreToLoad && searchType !== 'translation-tool'" style="margin-top: 40px; margin-bottom:30px;">
            <button class="btn btn-primary" @click.prevent="loadMore">Load More</button>
          </div>
        </div>

        <div>
          <SupportLinks />
          <BuildInfo />
        </div>

      </div><!-- end main-content-wrapper -->
    </div>

  </div>
  
</template>

<script>

  /*
    lower limit, store in local storage
    move history to local storage instead of cookie
    (show more...)
  */

  /* eslint-disable no-unused-vars */
  /* eslint-disable no-control-regex */
  /* eslint-disable no-console */

  import Definition from '../components/Definition';
  import DeveloperMessage from '../components/DeveloperMessage.vue';
  import ImageBanner from '../components/ImageBanner';
  import GeminiDraftTranslation from '../components/GeminiDraftTranslation.vue';
  import TranslationToolTextDisplay from '../components/TranslationToolTextDisplay.vue';
  import Match from '../components/Match';
  import SupportLinks from '../components/SupportLinks';
  import BuildInfo from '../components/BuildInfo.vue';
  import SearchInputBox from '../components/SearchInputBox.vue';
  import FirstTimeUserTutorial from '../components/FirstTimeUserTutorial.vue';
  import _ from 'lodash';
  import { mapMutations, mapState, mapGetters } from 'vuex'
  import uuidv1 from 'uuid/v1';
  import Vue from 'vue';
  import MonlamDefinition from "../components/MonlamDefinition";
  import Utils from "../utils"
  import vueConfig from '../../vue.config'
import { nextTick } from 'vue';
import store from '../store';
import { compact } from 'lodash';

  const DEFAULT_LIMIT = 100;

  const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  const USERNAME_REGEX = /^(?=.{2,20}$)(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+(?<![_.])$/;
  const PASSWORD_REGEX = /^.{2,}$/;

  const app = {
    name: 'app',
    components: {
      Definition,
      Match,
      ImageBanner,
      MonlamDefinition,
      DeveloperMessage,
      SupportLinks,
      BuildInfo,
      GeminiDraftTranslation,
      TranslationToolTextDisplay,
      SearchInputBox,
      FirstTimeUserTutorial
    },
    props: {
      searchTextQuery: String,
      searchTypeQuery: String
    },
    data: () => {
      return {
        largeFont: false,
        searchDefinitionAsWellAsTerm: false,
        results:'',
        // login/register state removed
        errorMessage:'',
        loading: false,
        searchHistory: [],
        searchHistoryBackButtonIndex:-1,
        searchOffset: 0,
        searchLimit: DEFAULT_LIMIT,
        noMoreToLoad: false,
        resultsTime: null,
        translationToolText: null,
        translationToolEditMode: false,
        dropdownElement: null,
        usingCache: false,
        preferencesReset: false,
        translationToolAreaColumnsSize: 4
      }
    },
    beforeCreate: function () {
      this.$server.socket.on('connect_error', (error) => {
        this.setError (error)
      });
      // State is automatically restored from localStorage by vuex-persist
    },
    created() {
      this.searchCache = [];
    },
    watch: {
      '$route': function (to, from) {
        const querySearchType = this.$route.query.searchType;
        const querySearchText = this.$route.query.searchText;
        const queryLimit = parseInt(this.$route.query.limit);
        if (querySearchType !== this.searchType || querySearchText !== querySearchText || queryLimit !== this.searchLimit) {
          this.processSavedSearchStateOrQueryParams()
        }
      }
    },
    async handleBackButton() {
      this.$log.debug("Back button")
      this.searchHistory = await this.$server.userService.getSearchHistory();
      this.$log.debug(`searchHistoryBackButtonIndex = ${this.searchHistoryBackButtonIndex}, searchHistory = `, this.searchHistory)
      this.searchHistoryBackButtonIndex += 1;
      await this.doShowHistoryItem(this.searchHistory[this.searchHistoryBackButtonIndex]);
    },
    beforeDestroy () {
      document.removeEventListener("backbutton", this.handleBackButton);
    },
    async mounted() {

      console.log("Mounted! Loading user from cookie...");

      if (Vue.prototype.$isTestingCypress) {
        console.log("Running in Cypress");
      }

      this.processSavedSearchStateOrQueryParams()
      this.searchHistory = await this.$server.userService.getSearchHistory();

      document.addEventListener("backbutton", this.handleBackButton, false);

      // Setup tutorial dropdown element reference
      this.$nextTick(() => {
        if (this.$refs.searchInputBox) {
          this.dropdownElement = this.$refs.searchInputBox.getDropdownElement();
        }
      });

    },
    computed: {
      ...mapState('user', [
        'username',
        'userId',
        'canEdit',
      ]),
      ...mapState('app', [
        'translationToolTextAreaFontSize',
        'colloquial'
      ]),
      // Check if device is mobile
      isMobileDevice() {
        return window.innerWidth <= 768 ||
               /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      },
      // Override layout for mobile devices
      translationToolLayout() {
        // Force stacked layout on mobile, otherwise use user preference
        return this.isMobileDevice ? 'stacked' : this.$store.state.app.translationToolLayout;
      },
      textAreaStyles() {
        return {
          fontSize: this.$store.state.app.translationToolTextAreaFontSize
        }
      },
      isEditedTranslationToolText() {
        return false; //this.$refs?.translationToolTextarea &&  this.ranslationToolText == this.$refs.translationToolTextarea.value;
      },
      translationToolTextAreaFontSize :{
        get() {
          return this.$store.state.app.translationToolTextAreaFontSize;
        },
        set(value) {
          console.log("Setting translation tool text area font size to", value);
          // Commit a mutation or dispatch an action to update the store
          this.$store.commit('app/setTranslationToolTextAreaFontSize', value);
        }
      },
      containsOrBeginsWith: {
        get() {
          return this.$store.state.app.containsOrBeginsWith;
        },
        set(value) {
          console.log("Setting translation tool text area font size to", value);
          // Commit a mutation or dispatch an action to update the store
          this.$store.commit('app/containsOrBeginsWith', value);
        }
      },
      compactResultsView: {
        get() {
          return this.$store.state.app.compactResultsView;
        },
        set(value) {
          this.$store.commit('app/setCompactResultsView', value);
        }
      },
      translationToolActive() {
        return this.searchType === 'translation-tool' && this.results && this.results.length > 0;
      },
      translationToolAreaClasses: function () {

        let classObj = {}

        if (this.translationToolActive && this.translationToolLayout === 'side-by-side') {
          classObj[`col-${this.translationToolAreaColumnsSize}`] = true;
          classObj['position-fixed'] = true;
          return classObj;
        }
        else if (this.translationToolActive && this.translationToolLayout === 'stacked') {
          return {sticky: true};
        }
        else {
          return {
            'col-12': true
          }
        }

      },
      mainSearchAndResultsClasses: function () {
        let classObj = {}

        if (this.translationToolActive && this.translationToolLayout === 'side-by-side') {
          const offsetSize = this.translationToolAreaColumnsSize;
          const columnSize = 12 - this.translationToolAreaColumnsSize;
          classObj[`col-${columnSize}`] = true;
          classObj[`offset-${offsetSize}`] = true;
          return classObj;
        }
        else {
          return {
            'col-12': true
          }
        }
      },
      searchText: {
        get () { return this.$store.state.app.searchText},
        set (value) {
          if (typeof value === 'string' || value instanceof String) {
            this.setSearchText(value.trim())
          }
          else {
            this.setSearchText(value)
          }
        }
      },
      searchTypeText: {
        get () { return this.$store.state.app.searchTypeText},
        set (value) { this.setSearchTypeText(value) }
      },
      searchType: {
        get () { return this.$store.state.app.searchType},
        set (value) { this.setSearchType(value) }
      },
      searchTextToLowerNoDiacritics() {
        return Utils.removeDiacritics(this.searchText).toLowerCase()
      },
      seachTextLength() {
        return this.searchText.length;
      },
    },
    methods: {
      ...mapMutations('app', [
        'setSearchText',
        'setSearchTypeText',
        'setSearchType',
        'setShowWylie',
        'toggleDictionarySection',
        'setTranslationToolLayout',
        'addSearchTypeToHistory'
      ]),
      ...mapMutations('user', [
        'loginUser',
        'logoutUser'
      ]),
      toggleCompactResultsView() {
        // not needed, no side effects, handled by v-model=compactResultsView
      },
      // Tutorial handler methods
      handleOpenDropdown() {
        if (this.$refs.searchInputBox) {
          this.$refs.searchInputBox.openDropdown();
        }
      },
      handleCloseDropdown() {
        if (this.$refs.searchInputBox) {
          this.$refs.searchInputBox.closeDropdown();
        }
      },
      handleTutorialDismissed() {
        console.log('Tutorial dismissed by user');
      },
      clearResults() {
        this.results = [];
        this.resultsTime = false;
        //this.noMoreToLoad = false;
      },
      // Login/Register methods removed. Now handled in LoginView.vue
      scrollToResult(resultIndex) {
        // Find the result element and scroll to it
        this.$nextTick(() => {
          const resultElements = document.querySelectorAll('.results-container');
          if (resultElements && resultElements[resultIndex]) {
            const element = resultElements[resultIndex];

            // In side-by-side layout, definitions are on the right and scroll independently
            // In stacked layout, we need to account for the fixed header
            if (this.translationToolLayout === 'side-by-side') {
              // Simple scroll - definitions have their own scroll area

              const elementRect = element.getBoundingClientRect();
              const offsetY = elementRect.top + window.pageYOffset - 100;

              window.scrollTo({
                top: offsetY,
                behavior: 'smooth'
              });

            } else {
              // Stacked layout - account for fixed header
              const fixedHeader = document.getElementById('translation-tool-text');
              const headerHeight = fixedHeader ? fixedHeader.offsetHeight : 0;

              const elementRect = element.getBoundingClientRect();
              const absoluteElementTop = elementRect.top + window.pageYOffset;
              const viewportHeight = window.innerHeight;
              const visibleHeight = viewportHeight - headerHeight;
              const middle = absoluteElementTop - headerHeight - (visibleHeight / 2) + (elementRect.height / 2);

              window.scrollTo({
                top: middle,
                behavior: 'smooth'
              });
            }

            // Add a temporary highlight effect
            element.style.backgroundColor = '#fffacd';
            setTimeout(() => {
              element.style.backgroundColor = '';
            }, 1500);
          }
        });
      },
      removeQueryString() {
          // Get the current URL
          const url = new URL(window.location.href);

          // Set the search property (query string) to an empty string
          url.search = '';

          // Use the History API to update the browser's URL without reloading
          // The first argument is the state object, the second is the title (often ignored),
          // and the third is the new URL
          window.history.replaceState({}, document.title, url.toString());
      },

      processSavedSearchStateOrQueryParams() {

        const normalize = (str) => {
          if (!str || typeof str !== 'string') return '';
          return str;
        };

        if (this.$route.query.resetSettings) {
          this.$router.replace({ query: {} });
          this.$store.commit('app/resetPreferences');
          this.preferencesReset = true;
          console.log("Resetting settings to default values");
          return;
        }

        // This has been updated to prioritize the component's current state values (which may have been set 
        // by user interaction) over the URL query parameters, to prevent overwriting user input when the route 
        // changes for reasons other than initial load or explicit query param changes.
        const loadedSearchType = normalize(this.searchType ? this.searchType : this.$route.query.searchType);
        const loadedSearchText = normalize(this.searchText ? this.searchText : this.$route.query.searchText);
        const loadedLimit = parseInt(this.searchLimit ? this.searchLimit : this.$route.query.limit);
        const loadedSearchTypeText = normalize(this.getSearchTypeText(loadedSearchType));

        console.log("Processing query params: ", {querySearchType: loadedSearchType, querySearchText: loadedSearchText, queryLimit: loadedLimit, querySearchTypeText: loadedSearchTypeText})

        // moved to SearchInputBox
        //this.largeFont = loadedSearchText && this.containsTibetanChars(loadedSearchText);

        if ((typeof loadedSearchType === "string" || loadedSearchType instanceof String) &&
            loadedSearchType !== "" &&
            loadedSearchTypeText !== null
        ) {
          this.setSearchType(loadedSearchType);
          this.setSearchTypeText(loadedSearchTypeText);
        }

        if (Number.isInteger(loadedLimit) && loadedLimit > 0) {
          this.setLimit(loadedLimit);
        }

        if (typeof loadedSearchText === "string" || loadedSearchText instanceof String) {
          this.setSearchText(loadedSearchText);
          this.handleSubmit();
        }
      },
      ltrim(str) {
        if(!str) return str;
        return str.replace(/^[\s\u0F0B]+/g, '');
      },
      handleContainsOrBeginsWith() {
        this.$log.debug(this.containsOrBeginsWith);
      },
      getUuid() {
        return uuidv1();
      },
      addToSearchCache(searchText, searchType, limit, results) {
        if (!this.usingCache) return;
        this.$log.debug("addToSearchCache: searchCache", this.searchCache);
        let newCache = this.searchCache.filter(item => item.searchText !== searchText || item.searchType !== searchType || item.searchLimit !== limit);
        newCache.push({
          searchText: searchText,
          searchType: searchType,
          searchLimit: limit,
          results: results
        });
        this.searchCache = newCache.slice(0,10);
        this.$log.debug("addToSearchCache: searchCache", this.searchCache)
      },
      getFromSearchCache(searchText, searchType, limit) {
        if (!this.usingCache) return null;
        this.$log.debug(`getFromSearchCache: searchText=${searchType}, searchType=${searchType}, limit=${limit}, searchCache`, this.searchCache)
        let found = this.searchCache.find(item => item.searchText === searchText && item.searchType === searchType && item.searchLimit === limit);
        if (found) {
          this.$log.debug("found", found);
          return found.results;
        }
        else {
          this.$log.debug("Not found in cache");
          return null;
        }
      },
      normalizeTibetanTerm(term) {
        // Remove brackets, trailing tsheg (་), and shad (།)
        return term.replace(/\[.*\]$/gm, '').replace(/[་།]+$/g, '');
      },
      shouldIgnoreTerm(tibetanText) {
        const normalizedTerm = this.normalizeTibetanTerm(tibetanText);

        // Check custom ignore list
        const customIgnoreList = this.$store.state.app.translationToolIgnoreList;
        const isInCustomList = customIgnoreList.some(ignoredTerm => {
          return this.normalizeTibetanTerm(ignoredTerm) === normalizedTerm;
        });

        if (isInCustomList) return true;

        // Check standard particles if enabled
        if (this.$store.state.app.ignoreStandardParticles) {
          const standardParticles = this.$store.state.app.standardParticles;
          const particleCategoriesEnabled = this.$store.state.app.particleCategoriesEnabled;

          // Check each category of particles
          for (const category of standardParticles) {
            // Only check particles if this category is enabled
            if (particleCategoriesEnabled[category.id]) {
              const isInCategory = category.particles.some(particle => {
                return this.normalizeTibetanTerm(particle) === normalizedTerm;
              });

              if (isInCategory) return true;
            }
          }
        }

        return false;
      },
      handleTermHidden(index) {
        // Get the Tibetan term from the item being hidden
        const tibetanTerm = this.results[index].tibetan.replace(/\[.*\]$/gm, '');

        // Remove all instances of this term from the results array
        this.results = this.results.filter(result => {
          const resultTerm = result.tibetan.replace(/\[.*\]$/gm, '');
          const normalizedResult = this.normalizeTibetanTerm(resultTerm);
          const normalizedHidden = this.normalizeTibetanTerm(tibetanTerm);
          return normalizedResult !== normalizedHidden;
        });

        // Update cache with new results
        this.addToSearchCache(this.searchText, this.searchType, this.searchLimit, this.results);
      },
      filterPaBaAlternatives(result) {
        if (!result._treeChildren || result._treeChildren.length === 0) return;

        const normalize = (t) => (t || '').replace(/\[.*\]$/gm, '').replace(/[་།]+$/g, '');

        const paBaRegex = /་པོ|བོ|པ|བ་?།?$/gm;

        function isPaBaVariant(a, b) {
          // Check པོ་/བོ་ (check longer suffixes first)
          //console.log("Comparing for pa/ba variants:", a, b);
          //console.log("Stripped:", a.replace(paBaRegex, ''), b.replace(paBaRegex, ''));
          if (a.replace(paBaRegex, '') === b.replace(paBaRegex, '')) {
            //console.log("They are pa/ba variants");
            return true;
          }
          return false;
        }

        const parentNorm = normalize(result.tibetan);

        // Filter against parent
        result._treeChildren = result._treeChildren.filter(child => {
          return !isPaBaVariant(parentNorm, normalize(child.tibetan));
        });

        // Filter among siblings: if two children are pa/ba variants, keep the longer one
        const kept = [];
        for (const child of result._treeChildren) {
          const childNorm = normalize(child.tibetan);
          const dominated = kept.some(k => isPaBaVariant(normalize(k.tibetan), childNorm) && normalize(k.tibetan).length >= childNorm.length);
          if (!dominated) {
            // Also remove any previously kept item that this one dominates
            for (let i = kept.length - 1; i >= 0; i--) {
              const kNorm = normalize(kept[i].tibetan);
              if (isPaBaVariant(kNorm, childNorm) && childNorm.length >= kNorm.length) {
                kept.splice(i, 1);
              }
            }
            kept.push(child);
          }
        }
        result._treeChildren = kept;

        if (result._treeChildren.length === 0) {
          delete result._treeChildren;
        }
      },
      buildResultsFromTree(tree) {
        return tree.map(node => {
          let result;
          if (node.isGreedy) {
            if (node.result && node.result.length > 0) {
              result = { ...node.result[0] };
            } else {
              result = {
                tibetan: node.tibetan,
                notFound: true,
                definiendum: ''
              };
            }
          } else {
            // Non-greedy alternative promoted to top level
            result = { ...node.result };
            result._isAlternative = true;
          }
          if (node.children && node.children.length > 0) {
            result._treeChildren = node.children.map(c => ({ ...c }));
          }
          return result;
        });
      },
      loadTranslateToolSubsearch(index, definiendumTibetan) {
        Vue.set(this.results[index], 'subsearchPerformed', true);
        this.results.splice(index+1, 0, {
          loading:true,
          tibetan: '',
          wylie: '',
          subsearchLevel: 1
        });
        this.$server.dictionaryService.find({query:{searchText:definiendumTibetan, searchType: 'translation-tool'}}).then(values => {
          values = values.slice(1);
          values = values.map(item => {
            item.subsearchLevel = (this.results[index].subsearchLevel ? this.results[index].subsearchLevel + 1 : 1);
            return item;
          });
          if (values && values.length > 0) {
            this.results.splice(index+1, 1, ...values);
            this.addToSearchCache(this.searchText, this.searchType,  this.searchLimit, this.results);
          }
        }).catch(error => {
          this.loading = false;
          this.$log.error(error.message);
          this.setError(error.message);
        });

      },
      setLimit(limit) {
        this.searchLimit = limit
        this.$log.debug("Setting search limit = " + this.searchLimit)
        if (this.searchType !== "translation-tool") {
          this.handleSubmit()
        }
      },
      getSearchTypeText(type) {

        let searches = {
          fulltext: "Full Text",
          'fulltext-all-fields': 'Full Text (all fields)',
          definiendum: 'Defined Term (contains)',
          englishDefinition: 'English/Definition',
          definiendumStarts: 'Defined Term (begins with)',
          definiendumExact: 'Defined Term (exact)',
          definiendumLike: 'Definiendum (LIKE)',
          definiendumRegex: 'Definiendum (regex)',
          definiendumGetRegexMatches: 'Definiendum (regex matches)',
          'wylie-to-unicode': 'Wylie to Unicode',
          'unicode-to-wylie': 'Unicode to Wylie',
          'translation-tool': 'Translation Tool',
          'monlam-englishToTibetan' : 'English to བོད་',
          'monlam-tibetanToEnglish' : 'བོད་ to English',
          'monlam-tibetanToTibetan' : 'སྨོན་ལམ་ཚིག་མཛོད་ཆེན་མོ། 2021',
          'monlam-tibetanToTibetanOld' : 'སྨོན་ལམ་ཚིག་མཛོད་ཆེན་མོ། 2016',
          'verb-tense': 'Verb Tense'
        };

        if (!(type in searches)) {
          return null;
        }
        else {
          return searches[type];
        }

      },
      updateSearch() {
        if (this.searchText !== '' && this.searchText.length > 1) {
          this.loading = true;
          this.doSearch(this.searchLimit, 0);
        }
      },
      setError(err) {
        this.errorMessage = err;
      },
      scoreForProp(obj, prop, score) {
        let propValue = _.get(obj, prop, false);
        if (propValue) {
          let strippedValue = Utils.removeDiacritics(propValue).toLowerCase();
          let numOccurances = (strippedValue.match(new RegExp(this.searchTextToLowerNoDiacritics, "g")) || []).length;

          if (strippedValue === this.searchTextToLowerNoDiacritics ) {
            score = 10;
          }
          else if (strippedValue.includes(this.searchTextToLowerNoDiacritics)) {
            let startsWith = strippedValue.startsWith(this.searchTextToLowerNoDiacritics);
            if (startsWith) {
              score = _.max([9*(this.seachTextLength * numOccurances) / strippedValue.length, score]);
            }
            else {
              score = _.max([8*(this.seachTextLength * numOccurances) / strippedValue.length, score]);
            }
          }
        }
        return score;
      },
      isClose(value, searchText) {
        if (!value) return false;
        let strippedValue = Utils.removeDiacritics(value).toLowerCase();
        if (strippedValue === searchText) return true;
        if (strippedValue.includes(searchText) && searchText.length / strippedValue.length > 0.5) return true;
        return false;
      },
      scoreUmaTibet(results) {
        let that = this;
        return _.map(results, function(value) {
          value.dictionary = "umatibet";

          if (value.isFavorite) {
            value.score = 1000;
          }
          else if (value.tibetan === that.searchText || value.definiendum === that.searchText || that.tibetan_normalized === that.searchText) {
            value.score = 1000;
          }
          else if (
            that.isClose(value.tibetan, that.searchTextToLowerNoDiacritics) ||
            that.isClose(value.tibetan_normalized, that.searchTextToLowerNoDiacritics) ||
            that.isClose(value.definiendum, that.searchTextToLowerNoDiacritics)) {
            value.score = 100;
          }
          else if (that.searchType === 'fulltext') {
            value.score = that.scoreForProp(value, 'tibetan', 0);
            value.score = that.scoreForProp(value, 'definiendum', value.score);
            value.score = that.scoreForProp(value, 'sanskrit', value.score);
            value.score = that.scoreForProp(value, 'english1', value.score);
            value.score = that.scoreForProp(value, 'rangjung_english', value.score);
          }
          else if (that.searchType === 'englishDefinition') {
            value.score = that.scoreForProp(value, 'english1', value.score);
            value.score = that.scoreForProp(value, 'rangjung_english', value.score);
          }
          else {
            value.score = that.scoreForProp(value, 'tibetan', 0);
            value.score = that.scoreForProp(value, 'definiendum', value.score);
            value.score = that.scoreForProp(value, 'sanskrit', value.score);
            value.score = that.scoreForProp(value, 'english1', value.score);
          }

          return value;

        });
      },
      mergeMatches(rows1, rows2) {

        function getCounts(arr, obj) {
          let counts = obj !== undefined ? obj : {};
          if (!arr || arr.length === 0) return counts;
          arr.forEach(value => {
            let match = value.regexp_matches.join(',');
            counts[match] = counts[match] ? counts[match] + 1 : 1;
          });
          return counts;
        }

        function sortObj(object) {
          var rv = [];
          for (var k in object) {
            if (Object.prototype.hasOwnProperty.call(object, "k")) rv.push({key: k, value:  object[k]});
          }
          rv.sort(function(o1, o2) {
            return o2.value - o1.value;
          });
          return rv;
        }

        let counts = getCounts(rows1, {});
        counts = getCounts(rows2, counts);

        let sortedCounts = sortObj(counts);

        return sortedCounts;

      },
      clearSearch(){
          this.results = [];
          this.largeFont = false;
          this.translationToolText = null;
          // Clear Gemini translation component
          if (this.$refs.geminiTranslation) {
            this.$refs.geminiTranslation.clearTranslation();
          }
      },
      handleSubmit() {

        if (this.searchText == '') {
          this.results = [];
          // Clear Gemini translation component
          if (this.$refs.geminiTranslation) {
            this.$refs.geminiTranslation.clearTranslation();
          }
        }
        else {
          // Clear previous Gemini translation when starting new search
          if (this.$refs.geminiTranslation) {
            this.$refs.geminiTranslation.clearTranslation();
          }

          // Validate and clean translation tool input
          if (this.searchType === 'translation-tool') {
            const tibetanRegex = /[\u0F00-\u0FFF]/;

            // Check if it contains any Tibetan characters at all
            if (!tibetanRegex.test(this.searchText)) {
              this.setError('Translation tool requires Tibetan script. Please enter text in Tibetan unicode.');
              this.loading = false;
              return;
            }

            // Filter out non-Tibetan characters (keep only Tibetan chars and whitespace)
            const cleanedText = this.searchText.replace(/[^\u0F00-\u0FFF\s]/g, '');

            // Update searchText with cleaned version if it was modified
            if (cleanedText !== this.searchText) {
              this.setSearchText(cleanedText);
            }
          }

          this.loading = true;
          this.results = [];
          this.searchOffset = 0;
          this.noMoreToLoad = false;
          this.doSearch(this.searchLimit, 0);
        }

      },
      loadMore() {
        this.loading = true;
        this.doSearch(this.searchLimit, this.searchOffset, true);
      },
      async doShowHistoryItem(historyItem) {
        this.loading = true;
        this.searchText = historyItem.searchText;
        this.updateSearchType(historyItem.searchType);
        this.searchOffset = 0;
        this.noMoreToLoad = false;
        await this.doSearch(this.searchLimit, 0);
      },
      updateSearchType(type) {
        console.log("CALLING REF to update search type to:", type)
        this.$refs.searchInputBox.updateSearchType(type);
      },
      async doSearch(limit, offset, loadingMore) {

        this.setError(null);

        let startTime = new Date().getTime();
        this.$log.info("Search started: " + startTime);

        const text = this.searchText;
        const type = this.searchType;

        this.$log.info(`Search: ${type} "${text}"`);
        this.$log.info(`Limit: ${limit}, Offset: ${offset}`);

        const _type = this.searchType.startsWith("monlam-") ? `${type}-${this.containsOrBeginsWith}` : this.searchType;

        if (!loadingMore) {

          let cached = this.getFromSearchCache(text, _type, this.searchLimit);

          if (cached) {
            this.$log.debug(`Using cache for ${_type}: ${text} : limit ${this.searchLimit}`);
            this.results = cached;
            this.loading = false;
            return;
          }
          else {
            this.$log.debug(`NOT using cache for ${_type}: ${text} : limit ${this.searchLimit}`);
          }

        }

        /*
          Handle Monlam Colloquial Searches
         */
        if (this.searchType.startsWith('monlam-')) {

          const trimmedSearchText = this.ltrim(this.searchText);

          const searchOptions = {
            query:{
              searchText:trimmedSearchText,
              limit:limit,
              offset:offset,
              searchType:this.containsOrBeginsWith,
              searchDefinitions: this.searchDefinitions,
              searchDefinitionAsWellAsTerm: this.searchDefinitionAsWellAsTerm
            }
          };

          let values = null;

          try {

            if (this.searchType === 'monlam-englishToTibetan') {
              values = await this.$server.monlamEnglishToTibetanService.find(searchOptions);
            }
            else if (this.searchType === 'monlam-tibetanToEnglish') {
              values = await this.$server.monlamTibetanToEnglishService.find(searchOptions);
            }
            else if (this.searchType === 'monlam-tibetanToTibetan') {
              values = await this.$server.monlamGrandDic.find(searchOptions);
            }
            else if (this.searchType === 'monlam-tibetanToTibetanOld') {
              values = await this.$server.monlamTibetanToTibetanOldService.find(searchOptions);
            }

            this.loading = false;
            let resultsTime = new Date().getTime();
            this.$log.info("Search results received in: " + (resultsTime - startTime)/1000);

            this.results = values;

            this.searchOffset += limit;

            if (this.results.length < limit) {
              this.noMoreToLoad = true;
            }

            this.addToSearchCache(this.searchText, this.searchType, this.searchLimit, this.results);
            this.$log.debug(`Saving to cache for ${type}: ${text}`);
          }
          catch (error) {
            this.loading = false;
            this.$log.error(error.message);
            this.setError(error.message);
          }

        }
        /*
          Handle UMA Tibet & Ranjung Yeshe Classical Searches
         */
        else {

          const queryParams = {
            searchText: this.searchText,
            searchType: this.searchType,
            limit:limit,
            offset:offset
          };

          this.$log.debug("search queryParams="+queryParams);

          await this.$server.dictionaryService.find({query:queryParams}).then(values => {

            this.loading = false;

            console.log("Raw search results: ", values);

            let resultsTime = new Date().getTime();
            this.$log.info("Search results received in: " + (resultsTime - startTime)/1000);

            if (this.searchType === 'definiendumGetRegexMatches') {
              this.results = values;
            }
            else if (this.searchType ==='translation-tool') {

              // Extract metadata (first element)
              const metadata = values[0];
              this.translationToolText = metadata.unicode;
              const tree = metadata.segmentationTree || [];

              // Build results from segmentation tree if available, otherwise flat
              if (tree.length > 0) {
                values = this.buildResultsFromTree(tree);
              } else {
                values = values.slice(1);
              }

              // set the subsearch level
              values = values.map(item => {
                item.subsearchLevel = 0;
                return item;
              });
              // Filter out ignored terms
              values = values.filter(item => {
                return !this.shouldIgnoreTerm(item.tibetan);
              });
              // Also filter children
              values.forEach(item => {
                if (item._treeChildren) {
                  item._treeChildren = item._treeChildren.filter(child =>
                    !this.shouldIgnoreTerm(child.tibetan)
                  );
                }
              });
              // Filter out trivial པ/བ alternatives
              values.forEach(item => this.filterPaBaAlternatives(item));
              // set the results
              this.results = values;

              // Generate Gemini draft translation (only if enabled in settings)
              if (this.$store.state.app.enableAiTranslation && this.$refs.geminiTranslation) {
                this.$refs.geminiTranslation.translate(this.translationToolText, null);
              }

              Vue.nextTick(() => {
                const textarea = this.$refs.translationToolTextarea;
                if (textarea) {
                  textarea.style.height = "";
                  textarea.style.height = textarea.scrollHeight +"px";
                }
              });

            }
            else {
              let umaTibet = this.scoreUmaTibet(values);
              if (loadingMore) {
                let previousNumResults = this.results.length - 1;
                this.$log.debug("previousNumResults: " + previousNumResults);
                var elmnt = document.getElementById("result-"+(previousNumResults-1));
                var scrollPosition = elmnt.scrollTop;
                this.results = this.results.concat(_.orderBy(umaTibet, ['score'], ['desc']));
                this.$nextTick(function () {
                  var elmnt = document.getElementById("result-"+(previousNumResults-1));
                  elmnt.scrollTop = scrollPosition;
                });
              }
              else {
                this.results = _.orderBy(umaTibet, ['score'], ['desc']);
              }

              let sortedTime = new Date().getTime();
              this.$log.info("Search results sorted in: " + (sortedTime - resultsTime)/1000);
              this.$log.info("Search total time: " + (sortedTime - startTime)/1000);
              this.resultsTime = `${(sortedTime - startTime)/1000} seconds`;
              this.$log.info("values: ", this.results);

              this.searchOffset += limit;

              if (this.results.length < limit) {
                this.noMoreToLoad = true;
              }
              
              this.addSearchTypeToHistory(this.searchType)
              this.addToSearchCache(this.searchText, this.searchType, this.searchLimit, this.results);
              this.$log.debug(`Saving to cache for ${type}: ${text}`);
            }
          }).catch(error => {
            this.loading = false;
            this.$log.error(error.message);
            this.setError(error.message);
          });

        }

        if (!loadingMore) {
          await this.$server.userService.addToSearchHistory(this.searchText, this.searchType, this.searchLimit);
          this.searchHistory = await this.$server.userService.getSearchHistory();
        }

        // we have to add the public path to the URL for deployed apps,
        // but don't want to add just a slash when using base path (such as, for local dev)
        const publicPath = vueConfig.publicPath.replace(/\/$/, "");
        const newUrl = `${publicPath}${this.$route.path}#/?${`searchText=${encodeURIComponent(this.searchText)}&searchType=${encodeURIComponent(this.searchType)}&limit=${encodeURIComponent(this.searchLimit)}`}`;
        this.$log.debug("newUrl="+newUrl)

        history.pushState(
            {},
            null,
            newUrl
        );
        //this.$log.debug("results: ", this.results);

      },
      handleClickTranslationToolDisplay() {
        window.scrollTo(0,0);
      },
      handleTextAreaChange(e) {
        const textarea = this.$refs.translationToolTextarea;
        textarea.style.height = "";
        textarea.style.height = (textarea.scrollHeight +10) +"px";
      },
      resizeTextAreaNextTick(target) {
        Vue.nextTick(() => {
          target.style.height = "";
          target.style.height = (target.scrollHeight +10) +"px";
        })
      },
      handleTextareaSubmit() {
        this.handleSubmit();
      },
      handleTextAreaInput(e) {
        this.searchText = e.target.value;
      },
      handleTextareaScrolltop(e) {
        window.scrollTo(0,0);
      },
      async handleWillEditTranslationToolText() {
        this.translationToolEditMode = true;
        Vue.nextTick(() => {
          const textAreaElem = this.$refs.translationToolTextarea;
          if (!textAreaElem) {
            console.error("Translation tool textarea element not found");
            return;
          }
          console.log("handleWillEditTranslationToolText: ", this.$refs);
          textAreaElem.style.height = 'auto';
          textAreaElem.style.height = textAreaElem.scrollHeight + 'px';
        })
      },  
      handleTextareaFontSizeDecrease(e) {
        Vue.nextTick(() => {
          let current =  this.translationToolTextAreaFontSize;
          current = parseFloat(current.replace("rem", ""));
          current = current - 0.1;
          this.translationToolTextAreaFontSize = current + 'rem';
        })
      },
      handleTextareaFontSizeIncrease(e) {
        Vue.nextTick(() => {
          let current =  this.translationToolTextAreaFontSize;
          current = parseFloat(current.replace("rem", ""));
          current = current + 0.1;
          this.translationToolTextAreaFontSize = current + 'rem';
        })
      },
    }
  };

  export default app;
</script>

<style>
  #app {
    font-family: 'Avenir', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
    margin-top: 60px;
  }

  #main-content-wrapper {
    min-width: 300px;
  }

  #dictionary-app.container-fluid {
    max-width: 100% !important;
  }

  #translation-tool-section.sticky {
    position: sticky;
    top: 61px;
    z-index: 10000;
    width: 100%;;
  }

  a {
    color:darkred;
  }

  a:hover, a:visited {
    color:darkred;
  }

  #allowPartialCheckbox {
    display: inline-block;
    width: inherit;
    font-size: 40px;
    margin-left: 5px;
  }

  .title-row {
    text-align: center;
    margin-top:100px;
    margin-bottom: 60px;
  }

  .no-margin-bottom {
    margin-bottom: 0px;
    margin-top: 30px;
  }


  #search-type-container {
    text-align: left;
  }

  .results-container:nth-child(even) {
    background: #fffae2 !important;
  }


  input[type=checkbox] {
    margin-right: 5px;
  }

  input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
  }

  .alert {
    width:50%;
    margin: 0 auto;
  }

  .row {
    margin-bottom: 20px;
  }

  .highlight {
    background-color: yellow;
  }

  #loading {
    height: 40px;
    margin-top:20px;
  }

  .results-loading-spinner {
    height: 40px;
    width: 40px;
    color: grey;
  }

  .wylie-floater {
    float: right;
    font-size: 30px;
    position: relative;
    top: 24px;
  }

  #show-favorites-container {
    margin-top: 20px;
  }

  .show-favorites, .hide-favorites, .clear-favorites, .clear-favorites-sure span {
    text-decoration: underline;
    cursor: pointer;
  }

  .hide-favorites, .clear-favorites, .clear-favorites-sure  {
    font-size: 1.0rem;
    color: gray;
  }

  .clear-favorites-sure span {
    margin-left: 7px;
  }

  h1 {
    font-size: 2.0rem;
  }

  #translation-tool-text {
    width: 100%;
    position: fixed;
    top: 55px;
    left: 0px;
    background-color: white;
    z-index: 100;
    filter: drop-shadow(0px 10px 4px #999);
    display: flex;
    flex-direction: column;
  }

  .translation-tool-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
    border-bottom: 1px solid #dee2e6;
  }

  .translation-tool-controls .btn-group {
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .translation-tool-controls .btn {
    font-weight: 500;
    transition: all 0.2s ease;
  }

  .translation-tool-controls .btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
  }

  .translation-tool-content {
    width: 100%;
  }

  .translation-tool-textarea {
    width: 100%;
    resize: none;
    min-height: 90px;
    padding: 10px;
    background: white;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: inherit;
    line-height: 1.6;
    min-height: 90px;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  #translation-tool-text textarea {
    width: 100%;
    height: 100%;
    border: none;
    padding: 5px 20px;
  }

  textarea:focus {
    outline: none;
  }

  /* Side-by-side layout */
  #translation-tool-text.side-by-side-layout {
    position: fixed;
    top: 55px;
    left: 0;
    width: 40%;
    height: calc(100vh - 55px);
    display: flex;
    flex-direction: column;
    filter: none;
    border-right: 2px solid #dee2e6;
  }

  #translation-tool-text.side-by-side-layout .translation-tool-content {
    flex: 1;
    overflow-y: auto;
    padding: 15px 20px;
  }

  .side-by-side-main-content {
    margin-left: 40%;
    padding-left: 20px;
  }

  /* Responsive: fall back to stacked on small screens */
  @media only screen and (max-width: 900px) {
    #translation-tool-text.side-by-side-layout {
      position: fixed;
      top: 55px;
      left: 0;
      width: 100%;
      height: auto;
      border-right: none;
      border-bottom: 2px solid #dee2e6;
      filter: drop-shadow(0px 10px 4px #999);
    }

    #translation-tool-text.side-by-side-layout .translation-tool-content {
      overflow-y: visible;
      max-height: 300px;
    }

    .side-by-side-main-content {
      margin-left: 0;
      padding-left: 0;
    }
  }

  .preferences-reset-notification {
    color: gray;
    font-family: monospace;
    text-transform: uppercase;
    text-align: center;
    width: 100%;
  }

  @media only screen
  and (max-device-width: 900px) {

    #app {
      margin-top: 20px;
    }

    .results-container .right-side{
      font-size: 90%;
    }

    h1 {
      font-size: 1.7em;
    }

    .no-margin-bottom {
      margin-bottom: 0px;
      margin-top: 10px;
    }

  }

  @media only screen
  and (max-device-width: 480px) {

    #app {
      margin-top: 20px;
    }

    #results-section {
      padding: 0px 10px;
    }

    .results-container {
      font-size: 80%;
    }

    h1 {
      font-size: 1.2em;
    }

    #allowPartialCheckboxHelp {
      display: none;
    }

    .no-margin-bottom {
      margin-bottom: 0px;
      margin-top: 10px;
    }

    .results-row {
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
    }

    #dictionary-app {
      padding: 0px !important;
    }

    .add-note-button, .add-note-done-button {
      margin: 10px;;
    }

  }

</style>
